import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { initAnalytics } from "../../helpers/analytics";
import { client } from "../../helpers/amplifyClient";
import { updateUsers } from "../../graphql/mutations";
import { updateUser } from "../../redux/auth/actions";
import { DEVICE_STORAGE_KEYS } from "../../constants/storage";
import { APICore } from "../../helpers/api/apiCore";

const api = new APICore();

const CookieBanner = () => {
  const { t } = useTranslation(["components", "common"], { useSuspense: false });
  const dispatch = useDispatch();
  const user = useSelector((state) => state.Auth?.user);

  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [consentOptions, setConsentOptions] = useState(() => {
    return {
      necessary: true,
      analytics: false,
    };
  });

  // Automatically hide if consent is already set
  useEffect(() => {
    let handled = false;
    let options = null;

    // Check 1: User settings
    if (user && user.settings) {
      try {
        const settings = typeof user.settings === "string" ? JSON.parse(user.settings) : user.settings;
        if (settings.cookieConsent) {
          options = settings.cookieConsent;
          handled = true;
        }
      } catch (e) {
        console.error("Could not parse user settings", e);
      }
    }

    // Check 2: LocalStorage fallback
    if (!handled) {
      const isConsentLocal = localStorage.getItem(DEVICE_STORAGE_KEYS.COOKIE_CONSENT);
      if (isConsentLocal !== null) {
        try {
          const parsed = JSON.parse(isConsentLocal);
          if (typeof parsed === "boolean") {
             options = { necessary: true, analytics: parsed };
          } else {
             options = parsed;
          }
          handled = true;
        } catch (e) {
          if (isConsentLocal === "true" || isConsentLocal === "false") {
             options = { necessary: true, analytics: isConsentLocal === "true" };
             handled = true;
          }
        }
      }
    }

    if (handled && options !== null) {
        // Fix: Save user settings to localStorage to avoid banner flashing on page reload
        localStorage.setItem(DEVICE_STORAGE_KEYS.COOKIE_CONSENT, JSON.stringify(options));
        setConsentOptions(options);
        initAnalytics(options);
        setShowBanner(false);
    } else {
        setShowBanner(true);
    }
  }, [user]);

  useEffect(() => {
    const handleOpenModal = () => setShowModal(true);
    window.addEventListener("show-cookie-banner", handleOpenModal);
    return () => window.removeEventListener("show-cookie-banner", handleOpenModal);
  }, []);

  const saveConsent = async (options) => {
    localStorage.setItem(DEVICE_STORAGE_KEYS.COOKIE_CONSENT, JSON.stringify(options));
    setConsentOptions(options);

    if (user) {
        // A copy of the current settings plus our own key — this writes the WHOLE blob back.
        let currentSettings = {};
        if (user.settings) {
            try {
                currentSettings =
                    typeof user.settings === "string"
                        ? JSON.parse(user.settings)
                        : { ...user.settings };
            } catch (e) {
                currentSettings = {};
            }
        }
        const newSettings = { ...currentSettings, cookieConsent: options };

        try {
            await client.graphql({
                query: updateUsers,
                variables: {
                    input: {
                        id: user.id,
                        settings: JSON.stringify(newSettings)
                    }
                }
            });
            // localStorage too, and as an OBJECT. This used to dispatch the JSON string form
            // and skip localStorage entirely, which is why every settings reader in this app
            // still has to tolerate a string — and why the local copy could fall behind Redux.
            api.setLoggedInUserInLocalStorage({ ...user, settings: newSettings });
            dispatch(updateUser({ ...user, settings: newSettings }));
        } catch (error) {
            console.error("Failed to save consent settings to user profile", error);
        }
    }

    initAnalytics(options);
    setShowBanner(false);
    setShowModal(false);
  };

  const handleAcceptAll = () => saveConsent({ necessary: true, analytics: true });
  const handleDeclineAll = () => saveConsent({ necessary: true, analytics: false });
  const handleSavePreferences = () => saveConsent(consentOptions);
  
  const toggleAnalytics = (e) => setConsentOptions({ ...consentOptions, analytics: e.target.checked });
  const handleManage = () => setShowModal(true);

  if (!showBanner && !showModal) {
    return null;
  }

  return (
    <>
      {showBanner && !showModal && (
        <div className="fixed-bottom p-3 p-md-4 border-top shadow-lg" style={{ zIndex: 1050, backgroundColor: "#fff" }}>
          <div className="container-fluid">
            <div className="row align-items-center">
              <div className="col-lg-8 col-12 mb-3 mb-lg-0">
                <p className="mb-0 text-muted">
                  {t(
                    "We use cookies and tracking technologies to improve your browsing experience, analyze site traffic, and understand where our visitors are coming from."
                  )}
                </p>
              </div>
              <div className="col-lg-4 col-12 d-flex justify-content-lg-end justify-content-center gap-2 flex-wrap text-nowrap">
                <Button variant="light" onClick={handleManage}>
                  {t("Manage")}
                </Button>
                <Button variant="outline-primary" onClick={handleDeclineAll}>
                  {t("Decline All")}
                </Button>
                <Button variant="primary" onClick={handleAcceptAll}>
                  {t("Accept All")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Advanced Settings Modal */}
      <Modal show={showModal} onHide={() => {
        if (!showBanner) setShowModal(false); 
      }} centered size="lg" backdrop="static">
        <Modal.Header closeButton={!showBanner}>
          <Modal.Title>{t("Cookie Preferences")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-4">
              {t("You can choose which categories of cookies you allow. Note that depending on your settings, some functionalities might become unavailable.")}
          </p>

          <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-1">
                  <h5 className="mb-0">{t("Strictly Necessary")}</h5>
                  <Form.Check type="switch" id="necessary-switch" checked={true} disabled />
              </div>
              <p className="text-muted small">
                  {t("These cookies are essential for the website to function properly and cannot be disabled. They include session cookies, security enhancements, and consent tracking.")}
              </p>
          </div>
          <hr />
          <div className="mb-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                  <h5 className="mb-0">{t("Analytics & Performance")}</h5>
                  <Form.Check type="switch" id="analytics-switch" checked={consentOptions.analytics} onChange={toggleAnalytics} />
              </div>
              <p className="text-muted small">
                  {t("Allow us to analyze user behavior (e.g. MS Clarity, Google Analytics) to improve our services and detect issues. Your data may be used to generate aggregated usage reports.")}
              </p>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between w-100">
          <Button variant="outline-secondary" onClick={handleDeclineAll}>
            {t("Decline All")}
          </Button>
          <div>
            <Button variant="primary" onClick={handleSavePreferences}>
                {t("Save Preferences")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CookieBanner;
